/**
 * Glowwa cart — custom subscription controls per line item.
 * Swaps linked products + selling plans via Cart API (remove line, re-add).
 */

/**
 * @returns {string}
 */
function getCartSectionIds() {
  return [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean)
    .join(',');
}

/**
 * @param {HTMLElement} root
 */
function closePanel(root) {
  const panel = root.querySelector('[data-glowwa-cart-subscription-panel]');
  const trigger = root.querySelector('[data-glowwa-cart-subscription-trigger]');
  if (panel instanceof HTMLElement) {
    panel.hidden = true;
  }
  if (trigger instanceof HTMLElement) {
    trigger.setAttribute('aria-expanded', 'false');
  }
}

/**
 * @param {HTMLElement} root
 */
function togglePanel(root) {
  const panel = root.querySelector('[data-glowwa-cart-subscription-panel]');
  const trigger = root.querySelector('[data-glowwa-cart-subscription-trigger]');
  if (!(panel instanceof HTMLElement) || !(trigger instanceof HTMLElement)) {
    return;
  }

  const willOpen = panel.hidden;
  panel.hidden = !willOpen;
  trigger.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
}

/**
 * @param {string} url
 * @param {Record<string, unknown>} body
 * @returns {Promise<Record<string, unknown>>}
 */
async function postCartJson(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  });

  const data = await response.json();
  if (!response.ok || data.errors) {
    const message =
      typeof data.errors === 'string'
        ? data.errors
        : data.description || 'Cart update failed';
    throw new Error(message);
  }

  return data;
}

/**
 * @param {Record<string, unknown>} sections
 */
function dispatchCartMorph(sections) {
  document.dispatchEvent(
    new CustomEvent('cart:update', {
      bubbles: true,
      detail: { data: { sections: sections ?? {} } },
    })
  );
}

/**
 * @param {HTMLElement} root
 * @param {{ variantId: string, sellingPlanId?: string }} config
 */
async function swapCartLine(root, config) {
  const line = parseInt(root.dataset.cartLine ?? '', 10);
  const quantity = parseInt(root.dataset.quantity ?? '1', 10);
  const variantId = parseInt(config.variantId, 10);
  const sellingPlanId = config.sellingPlanId
    ? parseInt(config.sellingPlanId, 10)
    : null;

  if (!line || !variantId) {
    return;
  }

  const sections = getCartSectionIds();
  const sectionsUrl = window.location.pathname;
  const changeUrl =
    window.Theme?.routes?.cart_change_url ?? '/cart/change.js';
  const addUrl = window.Theme?.routes?.cart_add_url ?? '/cart/add.js';

  root.classList.add('is-loading');

  try {
    await postCartJson(changeUrl, {
      line,
      quantity: 0,
      ...(sections && { sections }),
      sections_url: sectionsUrl,
    });

    const addBody = {
      id: variantId,
      quantity,
      ...(sellingPlanId && { selling_plan: sellingPlanId }),
      ...(sections && { sections }),
      sections_url: sectionsUrl,
    };

    const addResponse = await postCartJson(addUrl, addBody);
    dispatchCartMorph(addResponse.sections);
  } catch (error) {
    console.error('[glowwa-cart-subscription]', error);
  } finally {
    root.classList.remove('is-loading');
  }
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) {
    return;
  }

  const root = target.closest('[data-glowwa-cart-subscription]');
  if (!(root instanceof HTMLElement)) {
    return;
  }

  const trigger = target.closest('[data-glowwa-cart-subscription-trigger]');
  if (trigger && root.contains(trigger)) {
    event.preventDefault();
    togglePanel(root);
    return;
  }

  const subscribeBtn = target.closest('[data-glowwa-cart-subscription-subscribe]');
  if (subscribeBtn instanceof HTMLElement) {
    event.preventDefault();
    const variantId = subscribeBtn.dataset.variantId;
    const sellingPlanId = subscribeBtn.dataset.sellingPlanId;
    if (!variantId || !sellingPlanId) {
      return;
    }
    swapCartLine(root, { variantId, sellingPlanId });
    return;
  }

  const optionBtn = target.closest('[data-glowwa-cart-subscription-option]');
  if (optionBtn instanceof HTMLElement) {
    event.preventDefault();
    const variantId = optionBtn.dataset.variantId;
    if (!variantId) {
      return;
    }

    const mode = optionBtn.dataset.optionMode;
    if (mode === 'onetime') {
      swapCartLine(root, { variantId });
    } else {
      const sellingPlanId = optionBtn.dataset.sellingPlanId;
      if (!sellingPlanId) {
        return;
      }
      swapCartLine(root, { variantId, sellingPlanId });
    }

    closePanel(root);
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') {
    return;
  }

  const openPanel = document.querySelector(
    '[data-glowwa-cart-subscription-panel]:not([hidden])'
  );
  if (!(openPanel instanceof HTMLElement)) {
    return;
  }

  const root = openPanel.closest('[data-glowwa-cart-subscription]');
  if (root instanceof HTMLElement) {
    closePanel(root);
  }
});

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) {
    return;
  }

  if (target.closest('[data-glowwa-cart-subscription]')) {
    return;
  }

  document.querySelectorAll('[data-glowwa-cart-subscription]').forEach((root) => {
    if (root instanceof HTMLElement) {
      closePanel(root);
    }
  });
});
